import React, { useEffect } from 'react';
import { Button } from '../ui/button';
import { DISCOUNT_RATE } from '../../data/servicesData';

const fmt = (n) => `$${n.toLocaleString()}`;
const isMonthly = (service) => service.billing.startsWith('per month');

const QuoteSummary = ({ items, onSubmit }) => {
  useEffect(() => {
    document.body.classList.toggle('quote-bar-active', items.length > 0);
    return () => document.body.classList.remove('quote-bar-active');
  }, [items.length]);

  const monthly = items.filter((i) => isMonthly(i.service));
  const oneTime = items.filter((i) => !isMonthly(i.service));
  const sum = (list, key) => list.reduce((acc, i) => acc + i.tier[key], 0);
  const monthlyOur = sum(monthly, 'price');
  const monthlyMarket = sum(monthly, 'market');
  const oneTimeOur = sum(oneTime, 'price');
  const oneTimeMarket = sum(oneTime, 'market');
  const savings = monthlyMarket - monthlyOur + (oneTimeMarket - oneTimeOur);

  return (
    <>
    <aside className="lg:sticky lg:top-32 rounded-2xl bg-gray-900 text-white p-6 shadow-2xl" data-testid="quote-summary">
      <h2 className="text-base md:text-lg font-bold uppercase tracking-wide mb-4">Your quote</h2>

      {items.length === 0 ? (
        <p className="text-gray-400 text-sm" data-testid="quote-empty">Select at least one service to see your total.</p>
      ) : (
        <ul className="space-y-2 mb-5 text-sm" data-testid="quote-items">
          {items.map(({ service, tier }) => (
            <li key={service.slug} className="flex justify-between gap-3">
              <span className="text-gray-300">{service.title} <span className="text-gray-500">· {tier.name}</span></span>
              <span className="font-semibold whitespace-nowrap">{fmt(tier.price)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-white/10 pt-4 space-y-3">
        <Row label="Monthly recurring" market={monthlyMarket} our={monthlyOur} testId="quote-monthly-total" />
        <Row label="One-time projects" market={oneTimeMarket} our={oneTimeOur} testId="quote-onetime-total" />
      </div>

      <div className="mt-5 rounded-xl p-4" style={{ backgroundColor: '#D97904' }}>
        <p className="text-xs uppercase tracking-wide opacity-90">You save vs. market</p>
        <p className="text-3xl font-bold" data-testid="quote-savings">{fmt(savings)}</p>
        <p className="text-xs opacity-90">{Math.round(DISCOUNT_RATE * 100 * 10) / 10}% off Northwest Arkansas averages</p>
      </div>

      <Button
        data-testid="quote-submit-btn"
        disabled={items.length === 0}
        onClick={onSubmit}
        className="w-full mt-5 rounded-full py-6 text-base font-bold text-gray-900 bg-white hover:bg-gray-100 disabled:opacity-40"
      >
        Request This Quote
      </Button>
    </aside>
    {items.length > 0 && (
      <div
        data-testid="quote-mobile-bar"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-gray-900 text-white pl-4 pr-24 py-3 flex items-center justify-between gap-3 border-t border-white/10 shadow-2xl"
      >
        <div className="text-sm leading-tight">
          {monthlyOur > 0 && <p><span className="text-gray-400">Monthly</span> <span className="font-bold" style={{ color: '#FF9900' }}>{fmt(monthlyOur)}</span></p>}
          {oneTimeOur > 0 && <p><span className="text-gray-400">One-time</span> <span className="font-bold" style={{ color: '#FF9900' }}>{fmt(oneTimeOur)}</span></p>}
        </div>
        <Button
          data-testid="quote-mobile-submit-btn"
          onClick={onSubmit}
          className="rounded-full font-bold text-white px-5"
          style={{ backgroundColor: '#D97904' }}
        >
          Request Quote
        </Button>
      </div>
    )}
    </>
  );
};

const Row = ({ label, market, our, testId }) => (
  <div className="flex justify-between items-baseline">
    <span className="text-sm text-gray-300">{label}</span>
    <span className="text-right">
      {market > 0 && <span className="block text-xs text-gray-400 line-through">{fmt(market)}</span>}
      <span className="text-xl font-bold" data-testid={testId} style={{ color: '#FF9900' }}>{fmt(our)}</span>
    </span>
  </div>
);

export default QuoteSummary;
